import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'

import { colors, Colors } from '../constants/colors'

type ColorProp = { color?: keyof typeof colors }

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 125, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 125, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 125, 255, 0);
  }
`

export const Button = styled.button<{ disabled?: boolean } & ColorProp>`
  display: flex;
  height: 54px;
  width: 100%;
  padding: 16px 0px 17px 0px;
  justify-content: center;
  align-items: center;
  background-color: ${({ color }) => (color ? colors[color] : colors.blue)};
  color: ${colors.white};
  border-radius: 15px;
  font-size: 15px;
  font-style: normal;
  font-weight: 800;
  line-height: 140%;
  position: relative;
  overflow: hidden;

  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transform-origin: center;

  &:not(&:disabled) {
    cursor: pointer;
  }

  &:hover:not(&:disabled) {
    background-color: ${({ color }) =>
      color ? `${colors[color]}CC` : colors.hoverBlue};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(&:disabled) {
    transform: scale(0.95);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    opacity: 0.9;
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    background-image: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.3) 10%,
      transparent 10.01%
    );
    background-repeat: no-repeat;
    background-position: 50%;
    transform: scale(10, 10);
    opacity: 0;
    transition:
      transform 0.5s,
      opacity 0.8s;
  }

  &:active:after {
    transform: scale(0, 0);
    opacity: 0.2;
    transition: 0s;
  }

  &:focus-visible:not(:disabled) {
    outline: none;
    border: 2px solid ${colors.white}90;
    box-shadow: 0 0 0 2px ${colors.white}40;
  }
`

export const SocialButton = styled(Button)`
  background-color: ${colors.social};
  color: ${colors.blue};

  &:hover:not(&:disabled) {
    background-color: ${colors.social};
    opacity: 0.9;
    box-shadow: 0 3px 10px rgba(0, 125, 255, 0.08);
  }

  &:active:not(&:disabled) {
    background-color: ${colors.hoverBlue};
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  }
`

export const OutlineButton = styled.button`
  height: 40px;
  padding: 4px 15px;
  justify-content: center;
  color: #fff;
  text-align: center;
  font-family: Hauora;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 1rem;
  cursor: pointer;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover:not(&:disabled) {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.7);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  &:active:not(&:disabled) {
    transform: scale(0.96);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &:after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    background-image: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.2) 10%,
      transparent 10.01%
    );
    background-repeat: no-repeat;
    background-position: 50%;
    transform: scale(10, 10);
    opacity: 0;
    transition:
      transform 0.5s,
      opacity 0.8s;
  }

  &:active:after {
    transform: scale(0, 0);
    opacity: 0.2;
    transition: 0s;
  }
`

export const ConnectButton = styled(Button)`
  height: 40px;
  border-radius: 60px;
  display: inline-flex;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 4px;

  font-family: Hauora;
  font-size: 13px;
  font-style: normal;
  font-weight: 800;
  line-height: 140%;
  letter-spacing: -0.078px;

  &:hover:not(&:disabled) {
    animation: ${pulseAnimation} 1.5s infinite;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
  }

  &:active:not(&:disabled) {
    transform: scale(0.95);
    animation: none;
  }
`
